import { useState, useRef } from "react";

// ─── System prompts ──────────────────────────────────────────────────────────
const SYS_TECH = `你是台股技術分析師。對指定股票用 web_search 搜尋最新技術數據。
回傳純JSON不加說明：
{
  "name":"名稱","code":"代號","price":"收盤價數字",
  "trend":"多頭/空頭/盤整","trend_reason":"一句話",
  "kline":"多方/空方/中性","kline_reason":"一句話",
  "ma":"多頭排列/空頭排列/糾結","ma_reason":"一句話",
  "volume":"攻擊量/進貨量/縮量/出貨量/爆大量","volume_reason":"一句話",
  "ma5":數字,"ma10":數字,"ma20":數字,
  "support":"支撐價位數字","resistance":"壓力價位數字",
  "candles":[最近20個交易日,{"o":開,"h":高,"l":低,"c":收,"v":量相對比例0-100}],
  "data_quality":"高/中/低"
}
candles必須真實反映趨勢的20根K棒，v是相對成交量0-100。`;

const SYS_JUDGE = `技術分析選股Agent，依即時數據套用四大金剛+五步法。回傳純JSON：
{"ts":0-25,"ks":0-25,"ms":0-25,"vs":0-25,
"t1p":true或false,"rr":"淘汰原因或空",
"e2":true或false,"et":"進場型態","ec":"進場條件",
"sl":"停損條件","sp":"停利目標","rw":"風報比","op":"操作提醒",
"v":"✅可進場或⚠️等待訊號或❌淘汰",
"annotation_trend":{"bar":0-19,"label":"趨勢判斷點","type":"bull/bear/neutral"},
"annotation_kline":{"bar":0-19,"label":"K線訊號","type":"bull/bear/neutral"},
"annotation_ma":{"bar":0-19,"label":"均線訊號","type":"bull/bear/neutral"},
"annotation_vol":{"bar":0-19,"label":"量能訊號","type":"bull/bear/neutral"}
}`;

// ─── JSON repair ─────────────────────────────────────────────────────────────
function parseJSON(str) {
  const clean = str.replace(/```json|```/g, "").trim();
  const s = clean.indexOf("{"), e = clean.lastIndexOf("}");
  if (s === -1 || e === -1) throw new Error("No JSON");
  let raw = clean.slice(s, e + 1);
  try { return JSON.parse(raw); } catch (_) {}
  raw = raw
    .replace(/,\s*"[^"]*"\s*:\s*"[^"]*$/, "")
    .replace(/,\s*"[^"]*"\s*:\s*[^,}\]]*$/, "")
    .replace(/,\s*"[^"]*"\s*$/, "")
    .trimEnd();
  const stack = [];
  for (const ch of raw) { if (ch==="{") stack.push("}"); else if (ch==="[") stack.push("]"); else if (ch==="}"||ch==="]") stack.pop(); }
  raw += stack.reverse().join("");
  try { return JSON.parse(raw); } catch (_) {}
  const get = (k) => { const m = clean.match(new RegExp(`"${k}"\\s*:\\s*("([^"]*)"|(true|false)|(-?\\d+\\.?\\d*))`)); if (!m) return undefined; if (m[2]!==undefined) return m[2]; if (m[3]!==undefined) return m[3]==="true"; if (m[4]!==undefined) return parseFloat(m[4]); };
  return { ts:get("ts")??0, ks:get("ks")??0, ms:get("ms")??0, vs:get("vs")??0, t1p:get("t1p")??false, e2:get("e2")??false, et:get("et"), ec:get("ec"), sl:get("sl"), sp:get("sp"), rw:get("rw"), op:get("op"), rr:get("rr"), v:get("v")??"❌ 淘汰" };
}

// ─── API calls ────────────────────────────────────────────────────────────────
// ⚠️ 換成你的 Cloudflare Worker 網址
const PROXY_URL = "https://stock-proxy.smallbeer3.workers.dev";

async function callClaude(system, userMsg, useSearch = false) {
  const body = { model:"claude-sonnet-4-20250514", max_tokens:1000, system, messages:[{ role:"user", content:userMsg }] };
  if (useSearch) body.tools = [{ type:"web_search_20250305", name:"web_search" }];
  const res = await fetch(PROXY_URL, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body) });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.content?.filter(b => b.type==="text").map(b => b.text).join("") || "";
}

async function fetchTechData(query) {
  const txt = await callClaude(SYS_TECH, `搜尋 ${query} 近期技術數據、K線、均線、成交量、趨勢`, true);
  return parseJSON(txt);
}

async function judgeStock(name, code, tech) {
  const info = `股票：${name}(${code}) 收盤價：${tech.price}
趨勢：${tech.trend}（${tech.trend_reason}）
K線：${tech.kline}（${tech.kline_reason}）
均線：${tech.ma}（${tech.ma_reason}）MA5:${tech.ma5} MA10:${tech.ma10} MA20:${tech.ma20}
成交量：${tech.volume}（${tech.volume_reason}）
支撐：${tech.support} 壓力：${tech.resistance}`;
  const txt = await callClaude(SYS_JUDGE, `依四大金剛+五步法判斷，標註annotation:\n${info}`);
  return parseJSON(txt);
}

// ─── Candlestick Chart ────────────────────────────────────────────────────────
function CandleChart({ candles = [], annotations = [], support, resistance, price }) {
  if (!candles.length) return (
    <div style={{ height:130, display:"flex", alignItems:"center", justifyContent:"center", color:"#475569", fontSize:12 }}>無K線資料</div>
  );
  const W = 580, H = 130, VOL_H = 26, PAD = { l:8, r:8, t:14, b:4 };
  const n = candles.length;
  const barW = Math.floor((W - PAD.l - PAD.r) / n);
  const cW = Math.max(2, barW - 2);
  const chartH = H - PAD.t - PAD.b - VOL_H - 6;

  const prices = candles.flatMap(c => [c.h, c.l]);
  if (support && !isNaN(+support)) prices.push(+support);
  if (resistance && !isNaN(+resistance)) prices.push(+resistance);
  if (price && !isNaN(+price)) prices.push(+price);
  const minP = Math.min(...prices) * 0.998, maxP = Math.max(...prices) * 1.002;
  const pRange = maxP - minP || 1;
  const py = (p) => PAD.t + ((maxP - p) / pRange) * chartH;
  const maxV = Math.max(...candles.map(c => c.v || 0), 1);
  const vy = (v) => H - PAD.b - (v / maxV) * VOL_H;
  const bx = (i) => PAD.l + i * barW + barW / 2;

  // Compute MA lines from candles
  const computeMA = (period) => candles.map((_, i) => {
    if (i < period - 1) return null;
    const slice = candles.slice(i - period + 1, i + 1);
    return slice.reduce((s, c) => s + c.c, 0) / period;
  });
  const ma5v = computeMA(5), ma10v = computeMA(10), ma20v = computeMA(20);
  const maLines = [{ v:ma5v, color:"#fb923c" }, { v:ma10v, color:"#c084fc" }, { v:ma20v, color:"#60a5fa" }];

  const annColor = (t) => t==="bull"?"#4ade80":t==="bear"?"#f87171":"#fbbf24";
  const annBg   = (t) => t==="bull"?"rgba(74,222,128,.18)":t==="bear"?"rgba(248,113,113,.18)":"rgba(251,191,36,.18)";

  return (
    <div style={{ overflowX:"auto" }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:"block", minWidth:280 }}>
        {/* Grid */}
        {[.25,.5,.75].map(f => <line key={f} x1={PAD.l} x2={W-PAD.r} y1={PAD.t+f*chartH} stroke="#1e293b" strokeWidth="1" />)}

        {/* Support / Resistance */}
        {support && !isNaN(+support) && <line x1={PAD.l} x2={W-PAD.r} y1={py(+support)} stroke="#4ade80" strokeWidth="1" strokeDasharray="4,3" opacity=".7" />}
        {resistance && !isNaN(+resistance) && <line x1={PAD.l} x2={W-PAD.r} y1={py(+resistance)} stroke="#f87171" strokeWidth="1" strokeDasharray="4,3" opacity=".7" />}

        {/* Volume */}
        {candles.map((c,i) => (
          <rect key={`v${i}`} x={bx(i)-cW/2} y={vy(c.v||0)} width={cW} height={H-PAD.b-vy(c.v||0)}
            fill={c.c>=c.o?"#166534":"#991b1b"} opacity=".6" />
        ))}

        {/* MA lines */}
        {maLines.map(({ v, color }, mi) => {
          const pts = v.map((val,i) => val?`${bx(i)},${py(val)}`:null).filter(Boolean);
          return pts.length>1 ? <polyline key={mi} points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.2" opacity=".85" /> : null;
        })}

        {/* Candles */}
        {candles.map((c,i) => {
          const x=bx(i), isUp=c.c>=c.o, col=isUp?"#22c55e":"#ef4444";
          const top=py(Math.max(c.o,c.c)), bot=py(Math.min(c.o,c.c)), bH=Math.max(1,bot-top);
          return (
            <g key={`c${i}`}>
              <line x1={x} x2={x} y1={py(c.h)} y2={py(c.l)} stroke={col} strokeWidth="1" />
              <rect x={x-cW/2} y={top} width={cW} height={bH} fill={col} />
            </g>
          );
        })}

        {/* Current price */}
        {price && !isNaN(+price) && <>
          <line x1={PAD.l} x2={W-PAD.r} y1={py(+price)} stroke="#818cf8" strokeWidth="1" strokeDasharray="2,2" opacity=".7" />
          <rect x={W-PAD.r-30} y={py(+price)-7} width={30} height={13} fill="#4f46e5" rx="2" />
          <text x={W-PAD.r-15} y={py(+price)+3} fill="#fff" fontSize="7" textAnchor="middle">{price}</text>
        </>}

        {/* Annotations */}
        {annotations.map((ann, ai) => {
          if (ann.bar==null||ann.bar<0||ann.bar>=candles.length) return null;
          const c=candles[ann.bar], x=bx(ann.bar), yTop=py(c.h)-4;
          const col=annColor(ann.type), bg=annBg(ann.type);
          const icons=["📈","🕯️","〰️","📊"]; const icon=icons[ai]||"●";
          const lw=Math.min(88,(ann.label||"").length*5.2+14);
          const lx=Math.min(W-PAD.r-lw, Math.max(PAD.l, x-lw/2));
          const ly=yTop-20-ai*17;
          return (
            <g key={`ann${ai}`}>
              <line x1={x} x2={x} y1={Math.max(ly+12,yTop-2)} y2={yTop} stroke={col} strokeWidth="1" strokeDasharray="2,2" opacity=".8" />
              <circle cx={x} cy={yTop} r="3.5" fill={col} />
              <rect x={lx} y={ly-10} width={lw} height={13} fill={bg} rx="3" stroke={col} strokeWidth=".6" />
              <text x={lx+3} y={ly} fill={col} fontSize="6.5" fontWeight="bold">{icon} {ann.label}</text>
            </g>
          );
        })}

        {/* Legend */}
        <g transform={`translate(${PAD.l},${H-1})`}>
          {[["MA5","#fb923c"],["MA10","#c084fc"],["MA20","#60a5fa"]].map(([lb,c],i)=>(
            <g key={lb} transform={`translate(${i*40},0)`}>
              <line x1="0" x2="7" y1="-3" y2="-3" stroke={c} strokeWidth="1.5" />
              <text x="9" y="0" fill={c} fontSize="6.5">{lb}</text>
            </g>
          ))}
          <text x="126" y="0" fill="#4ade80" fontSize="6.5" opacity=".8">── 支撐</text>
          <text x="162" y="0" fill="#f87171" fontSize="6.5" opacity=".8">── 壓力</text>
        </g>
      </svg>
    </div>
  );
}

// ─── UI helpers ───────────────────────────────────────────────────────────────
const C = { g:"#16a34a", y:"#ca8a04", r:"#dc2626", b:"#2563eb", p:"#7c3aed", gr:"#6b7280" };
const STEPS = ["選股","進場","停損","操作","停利"];
const STEP_C = [C.b, C.g, C.r, C.y, C.p];
const STEP_BG = ["#dbeafe","#dcfce7","#fee2e2","#fef9c3","#ede9fe"];

function Bar({ score }) {
  const pct = (score/25)*100, color=pct>=72?C.g:pct>=44?C.y:C.r;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5 }}>
      <div style={{ flex:1, height:5, background:"#e5e7eb", borderRadius:99 }}>
        <div style={{ width:`${pct}%`, height:"100%", background:color, borderRadius:99 }} />
      </div>
      <span style={{ fontSize:12, fontWeight:700, color, minWidth:20, textAlign:"right" }}>{score}</span>
    </div>
  );
}

function Pill({ n, label, pass, fail, active }) {
  const bg=fail?"#fee2e2":pass?"#dcfce7":active?"#dbeafe":"#f3f4f6";
  const color=fail?C.r:pass?C.g:active?C.b:C.gr;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:3, padding:"3px 7px", borderRadius:99, background:bg }}>
      <div style={{ width:13, height:13, borderRadius:"50%", background:color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:7, fontWeight:700, color:"#fff" }}>{n}</div>
      <span style={{ fontSize:10, fontWeight:600, color }}>{label}{pass?" ✓":fail?" ✗":""}</span>
    </div>
  );
}

function GuardianTag({ icon, label, value, type }) {
  const color=type==="bull"?C.g:type==="bear"?C.r:C.y;
  const bg=type==="bull"?"#dcfce7":type==="bear"?"#fee2e2":"#fef9c3";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 8px", borderRadius:99, background:bg }}>
      <span style={{ fontSize:12 }}>{icon}</span>
      <div>
        <div style={{ fontSize:9, color:C.gr, lineHeight:1 }}>{label}</div>
        <div style={{ fontSize:11, fontWeight:700, color, lineHeight:1.2 }}>{value}</div>
      </div>
    </div>
  );
}

function StockCard({ item, rank, onRemove }) {
  const [open, setOpen] = useState(false);

  if (item.phase === "fetching_tech") return (
    <div style={{ border:"1px solid #dbeafe", borderRadius:10, padding:"12px 14px", marginBottom:8, background:"#eff6ff" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:16, display:"inline-block", animation:"spin 1s linear infinite" }}>⟳</span>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:600 }}>{item.query}</div>
          <div style={{ fontSize:11, color:C.b }}>🔍 搜尋即時技術數據與K線…</div>
        </div>
      </div>
    </div>
  );

  if (item.phase === "judging") return (
    <div style={{ border:"1px solid #dcfce7", borderRadius:10, overflow:"hidden", marginBottom:8 }}>
      <div style={{ padding:"12px 14px", background:"#f0fdf4", display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:16, display:"inline-block", animation:"spin 1s linear infinite" }}>⟳</span>
        <div>
          <div style={{ fontSize:14, fontWeight:600 }}>{item.tech?.name || item.query} <span style={{ fontSize:12, color:"#9ca3af" }}>{item.tech?.code}</span></div>
          <div style={{ fontSize:11, color:C.g }}>⚖️ 四大金剛評分中…</div>
        </div>
      </div>
      {item.tech?.candles?.length > 0 && (
        <div style={{ padding:"0 10px 10px", background:"#f0fdf4" }}>
          <div style={{ background:"#0f172a", borderRadius:8, padding:"8px 6px 4px" }}>
            <CandleChart candles={item.tech.candles} support={item.tech.support} resistance={item.tech.resistance} price={item.tech.price} annotations={[]} />
          </div>
        </div>
      )}
    </div>
  );

  if (item.phase === "error") return (
    <div style={{ border:"1px solid #fecaca", borderRadius:10, padding:"11px 14px", marginBottom:8, background:"#fff5f5", display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:14, fontWeight:600 }}>{item.query}</div>
        <div style={{ fontSize:12, color:C.r, marginTop:3 }}>⚠ {item.error}</div>
      </div>
      <button onClick={onRemove} style={{ padding:"4px 8px", border:"1px solid #fecaca", borderRadius:6, background:"#fff5f5", color:C.r, cursor:"pointer", fontSize:12 }}>移除</button>
    </div>
  );

  const tech = item.tech || {}, judge = item.judge || {};
  const s1=judge.t1p, s2=s1&&judge.e2;
  const total=(judge.ts??0)+(judge.ks??0)+(judge.ms??0)+(judge.vs??0);
  const tc=total>=70?C.g:total>=45?C.y:C.r;
  const verdict=judge.v||(s1?"⚠️ 等待訊號":"❌ 淘汰");
  const vSt=verdict.includes("✅")?{bg:"#dcfce7",c:C.g}:verdict.includes("⚠️")?{bg:"#fef9c3",c:C.y}:{bg:"#fee2e2",c:C.r};
  const chg=parseFloat(item.change||0);
  const annots=[judge.annotation_trend,judge.annotation_kline,judge.annotation_ma,judge.annotation_vol].filter(Boolean);

  return (
    <div style={{ border:`1px solid ${s1?"#e5e7eb":"#fecaca"}`, borderRadius:12, background:"#fff", overflow:"hidden", marginBottom:10 }}>
      {/* Header */}
      <div style={{ padding:"12px 14px", display:"flex", alignItems:"center", gap:9 }}>
        <div style={{ width:25, height:25, borderRadius:"50%", background:!s1?"#f3f4f6":rank<=3?"#fef3c7":"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:!s1?C.gr:rank<=3?"#92400e":C.gr, flexShrink:0 }}>
          {!s1?"✗":rank}
        </div>
        <div style={{ flex:1, minWidth:0, cursor:"pointer" }} onClick={() => setOpen(o=>!o)}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4, flexWrap:"wrap" }}>
            <span style={{ fontSize:15, fontWeight:700 }}>{tech.name || item.query}</span>
            {tech.code && <span style={{ fontSize:12, color:"#9ca3af" }}>{tech.code}</span>}
            {tech.price && <span style={{ fontSize:13, fontWeight:700 }}>NT${tech.price}</span>}
            {item.change && <span style={{ fontSize:11, fontWeight:600, color:chg>0?C.r:chg<0?C.g:C.gr }}>{chg>0?"▲":chg<0?"▼":""}{item.change}</span>}
            {tech.data_quality && <span style={{ fontSize:10, color:tech.data_quality==="高"?C.g:tech.data_quality==="中"?C.y:C.r }}>資料{tech.data_quality}</span>}
          </div>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            <GuardianTag icon="📈" label="趨勢" value={tech.trend||"—"} type={tech.trend==="多頭"?"bull":tech.trend==="空頭"?"bear":"neutral"} />
            <GuardianTag icon="🕯️" label="K線" value={tech.kline||"—"} type={tech.kline==="多方"?"bull":tech.kline==="空方"?"bear":"neutral"} />
            <GuardianTag icon="〰️" label="均線" value={tech.ma||"—"} type={tech.ma==="多頭排列"?"bull":tech.ma==="空頭排列"?"bear":"neutral"} />
            <GuardianTag icon="📊" label="量能" value={tech.volume||"—"} type={["攻擊量","進貨量"].includes(tech.volume)?"bull":["出貨量","爆大量"].includes(tech.volume)?"bear":"neutral"} />
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4, flexShrink:0 }}>
          <span style={{ background:vSt.bg, color:vSt.c, fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:99, whiteSpace:"nowrap" }}>{verdict}</span>
          <div style={{ display:"flex", alignItems:"baseline", gap:2 }}>
            <span style={{ fontSize:20, fontWeight:700, color:tc }}>{total}</span>
            <span style={{ fontSize:10, color:"#9ca3af" }}>/100</span>
          </div>
        </div>
        <span style={{ color:"#9ca3af", fontSize:12, cursor:"pointer", flexShrink:0 }} onClick={() => setOpen(o=>!o)}>{open?"▲":"▼"}</span>
      </div>

      {/* Chart — always visible */}
      {tech.candles?.length > 0 && (
        <div style={{ padding:"0 10px 10px" }}>
          <div style={{ background:"#0f172a", borderRadius:10, padding:"10px 8px 6px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5, paddingLeft:2 }}>
              <span style={{ fontSize:10, fontWeight:700, color:"#64748b" }}>K線圖 · 近20日</span>
              <div style={{ display:"flex", gap:8 }}>
                {tech.support && <span style={{ fontSize:9, color:"#4ade80" }}>支撐 {tech.support}</span>}
                {tech.resistance && <span style={{ fontSize:9, color:"#f87171" }}>壓力 {tech.resistance}</span>}
              </div>
            </div>
            <CandleChart candles={tech.candles} support={tech.support} resistance={tech.resistance} price={tech.price} annotations={open ? annots : []} />
            {open && annots.length > 0 && (
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:6, paddingLeft:2 }}>
                {annots.map((ann, i) => {
                  const icons=["📈","🕯️","〰️","📊"], names=["趨勢","K線","均線","量能"];
                  const color=ann.type==="bull"?"#4ade80":ann.type==="bear"?"#f87171":"#fbbf24";
                  return (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:3, background:"#1e293b", borderRadius:6, padding:"3px 7px" }}>
                      <span style={{ fontSize:10 }}>{icons[i]}</span>
                      <span style={{ fontSize:9, color:"#94a3b8" }}>{names[i]}：</span>
                      <span style={{ fontSize:9, fontWeight:600, color }}>{ann.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step pills */}
      <div style={{ padding:"0 14px 10px", display:"flex", gap:3, flexWrap:"wrap", alignItems:"center" }}>
        <Pill n="1" label="選股" pass={s1} fail={!s1} />
        {s1&&<><span style={{ color:"#d1d5db", fontSize:10 }}>›</span><Pill n="2" label="進場" pass={s2} active={!s2} /></>}
        {s1&&<><span style={{ color:"#d1d5db", fontSize:10 }}>›</span><Pill n="3" label="停損" pass /></>}
        {s1&&<><span style={{ color:"#d1d5db", fontSize:10 }}>›</span><Pill n="4" label="操作" pass /></>}
        {s1&&<><span style={{ color:"#d1d5db", fontSize:10 }}>›</span><Pill n="5" label="停利" pass /></>}
        {!open && <span style={{ marginLeft:"auto", fontSize:10, color:C.gr, cursor:"pointer" }} onClick={()=>setOpen(true)}>展開詳情 ▼</span>}
      </div>

      {/* Detail */}
      {open && (
        <div style={{ borderTop:"1px solid #f1f5f9" }}>
          <div style={{ padding:"12px 14px", borderBottom:"1px solid #f1f5f9" }}>
            <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:10 }}>
              <div style={{ width:18, height:18, borderRadius:"50%", background:s1?C.g:C.r, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:"#fff" }}>1</div>
              <span style={{ fontSize:13, fontWeight:700 }}>STEP 1｜四大金剛評分</span>
              <span style={{ fontSize:11, fontWeight:600, color:s1?C.g:C.r }}>{s1?"✓ 通過":"✗ 淘汰"}</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {[["📈 趨勢",tech.trend_reason,judge.ts],["🕯️ K線",tech.kline_reason,judge.ks],["〰️ 均線",tech.ma_reason,judge.ms],["📊 量能",tech.volume_reason,judge.vs]].map(([lb,reason,sc])=>(
                <div key={lb}><div style={{ fontSize:11, fontWeight:700, marginBottom:3 }}>{lb}</div><Bar score={sc??0} /><div style={{ fontSize:11, color:C.gr, marginTop:2, lineHeight:1.4 }}>{reason||"—"}</div></div>
              ))}
            </div>
            {!s1&&judge.rr&&<div style={{ marginTop:9, background:"#fee2e2", borderRadius:6, padding:"7px 10px", fontSize:12, color:C.r }}>淘汰：{judge.rr}</div>}
          </div>

          {s1 && <>
            <div style={{ padding:"11px 14px", borderBottom:"1px solid #f1f5f9", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div><div style={{ fontSize:12, fontWeight:700, marginBottom:6, color:C.b }}>STEP 2｜進場</div>
                <div style={{ fontSize:13, fontWeight:700, color:s2?C.g:C.y, marginBottom:4 }}>{judge.et||"等待訊號"}</div>
                <div style={{ fontSize:11, color:"#374151", lineHeight:1.5 }}>{judge.ec||"—"}</div></div>
              <div><div style={{ fontSize:12, fontWeight:700, marginBottom:6, color:C.r }}>STEP 3｜停損</div>
                <div style={{ fontSize:12, color:"#374151", lineHeight:1.5 }}>{judge.sl||"—"}</div></div>
            </div>
            <div style={{ padding:"11px 14px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div><div style={{ fontSize:12, fontWeight:700, marginBottom:6, color:C.b }}>STEP 4｜操作</div>
                <div style={{ fontSize:11, color:"#374151", lineHeight:1.6 }}>持倉：均線多頭<br/>減碼：跌破10日<br/>全出：跌破20日</div></div>
              <div><div style={{ fontSize:12, fontWeight:700, marginBottom:6, color:C.p }}>STEP 5｜停利</div>
                <div style={{ marginBottom:4 }}><span style={{ fontSize:10, color:C.gr }}>目標：</span><span style={{ fontSize:13, fontWeight:700, color:C.g }}>{judge.sp||"—"}</span></div>
                <div><span style={{ fontSize:10, color:C.gr }}>風報比：</span><span style={{ fontSize:13, fontWeight:700, color:C.b }}>{judge.rw||"—"}</span></div></div>
            </div>
          </>}
          {judge.op&&<div style={{ padding:"9px 14px", background:"#f8fafc", fontSize:12, color:"#374151", fontStyle:"italic", borderTop:"1px solid #f1f5f9" }}>💡 {judge.op}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [input, setInput] = useState("");
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const nextId = useRef(1);
  const inputRef = useRef(null);

  const parse = (raw) => raw.split(/[\n,、，]+/).map(s => s.trim()).filter(Boolean);

  const add = () => {
    const qs = parse(input);
    if (!qs.length) { setErr("請輸入股票名稱或代號"); return; }
    setErr("");
    setItems(prev => {
      const existing = new Set(prev.map(i => i.query));
      return [...prev, ...qs.filter(q => !existing.has(q)).map(q => ({ id: nextId.current++, query:q, phase:"idle" }))];
    });
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const onKey = (e) => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); add(); } };

  const removeItem = (id) => setItems(p => p.filter(x => x.id !== id));

  const update = (id, patch) => setItems(p => p.map(x => x.id===id ? { ...x, ...patch } : x));

  const runAgent = async () => {
    const targets = items.filter(i => i.phase==="idle" || i.phase==="error");
    if (!targets.length) return;
    setRunning(true);

    for (const t of targets) {
      update(t.id, { phase:"fetching_tech" });
      let tech;
      try {
        tech = await fetchTechData(t.query);
        update(t.id, { phase:"judging", tech });
      } catch(e) {
        update(t.id, { phase:"error", error:`技術數據失敗：${e.message}` });
        continue;
      }
      try {
        const judge = await judgeStock(tech.name||t.query, tech.code||"", tech);
        update(t.id, { phase:"done", judge });
      } catch(e) {
        update(t.id, { phase:"error", error:`評分失敗：${e.message}` });
      }
    }
    setRunning(false);
  };

  const idleItems = items.filter(i => i.phase==="idle");
  const doneItems = items.filter(i => i.phase==="done");
  const passItems = doneItems.filter(i => i.judge?.t1p);
  const sorted = [...passItems].sort((a,b) => {
    const ta=(a.judge?.ts??0)+(a.judge?.ks??0)+(a.judge?.ms??0)+(a.judge?.vs??0);
    const tb=(b.judge?.ts??0)+(b.judge?.ks??0)+(b.judge?.ms??0)+(b.judge?.vs??0);
    return tb-ta;
  });
  const elim = doneItems.filter(i => !i.judge?.t1p);
  const getRank = (item) => sorted.findIndex(s => s.id===item.id)+1;

  const canEnter = doneItems.filter(i => i.judge?.v?.includes("✅")).length;
  const waiting  = doneItems.filter(i => i.judge?.v?.includes("⚠️")).length;
  const out      = doneItems.filter(i => i.judge?.v?.includes("❌")).length;

  const inProgress = items.filter(i => ["fetching_tech","judging"].includes(i.phase));
  const errors = items.filter(i => i.phase==="error");
  const displayItems = [...inProgress, ...sorted, ...elim, ...errors, ...idleItems];

  return (
    <div style={{ fontFamily:"system-ui, sans-serif", maxWidth:700, margin:"0 auto", padding:"20px 16px" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>

      {/* Header */}
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:".1em", color:"#9ca3af", marginBottom:4 }}>即時技術數據 × 四大金剛線圖標註 × 五步法</div>
        <div style={{ fontSize:22, fontWeight:700, marginBottom:8 }}>選股 Agent</div>
        <div style={{ display:"flex", alignItems:"center", gap:3, flexWrap:"wrap" }}>
          {STEPS.map((s,i) => (
            <div key={s} style={{ display:"flex", alignItems:"center", gap:3 }}>
              <div style={{ padding:"3px 8px", background:STEP_BG[i], borderRadius:99, fontSize:11, fontWeight:600, color:STEP_C[i] }}>{i+1} {s}</div>
              {i<4&&<span style={{ color:"#d1d5db", fontSize:11 }}>›</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Input */}
      <div style={{ marginBottom:14 }}>
        <div style={{ display:"flex", gap:8, marginBottom:6 }}>
          <input ref={inputRef} value={input}
            onChange={e => { setInput(e.target.value); setErr(""); }}
            onKeyDown={onKey}
            placeholder="輸入股票名稱或代號，Enter 新增（逗號可分隔多支）"
            style={{ flex:1, padding:"10px 14px", border:`1px solid ${err?C.r:"#e5e7eb"}`, borderRadius:8, fontSize:14 }} />
          <button onClick={add}
            style={{ padding:"10px 16px", borderRadius:8, border:"none", background:"#374151", color:"#fff", fontSize:14, fontWeight:600, cursor:"pointer" }}>新增</button>
        </div>
        {err && <div style={{ fontSize:12, color:C.r, marginBottom:6 }}>{err}</div>}

        {/* Idle queue */}
        {idleItems.length > 0 && (
          <div style={{ border:"1px solid #e5e7eb", borderRadius:10, overflow:"hidden", marginBottom:10 }}>
            {idleItems.map((item,i) => (
              <div key={item.id} style={{ padding:"9px 14px", borderBottom:i<idleItems.length-1?"1px solid #f3f4f6":"none", display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:20, height:20, borderRadius:"50%", background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:C.gr, flexShrink:0 }}>{i+1}</div>
                <span style={{ flex:1, fontSize:14, fontWeight:600 }}>{item.query}</span>
                <button onClick={() => removeItem(item.id)} style={{ padding:"3px 8px", border:"1px solid #fee2e2", borderRadius:6, background:"#fff5f5", color:C.r, cursor:"pointer", fontSize:12 }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {idleItems.length > 0 && !running && (
          <button onClick={runAgent}
            style={{ width:"100%", padding:"12px 0", borderRadius:10, border:"none", background:"#1d4ed8", color:"#fff", fontSize:15, fontWeight:700, cursor:"pointer" }}>
            🤖 分析 {idleItems.length} 支股票
          </button>
        )}
      </div>

      {/* Summary */}
      {doneItems.length > 0 && (
        <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ flex:1, fontSize:13, color:C.gr }}>完成 {doneItems.length} 支｜通過 {passItems.length} 支</div>
          <div style={{ display:"flex", gap:14 }}>
            {[{n:canEnter,l:"✅ 可進場",c:C.g},{n:waiting,l:"⚠️ 等待",c:C.y},{n:out,l:"❌ 淘汰",c:C.r}].map(({n,l,c})=>(
              <div key={l} style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:17, fontWeight:700, color:c }}>{n}</span>
                <span style={{ fontSize:11, color:C.gr }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cards */}
      {displayItems.map((item,i) => (
        <StockCard key={item.id} item={item}
          rank={item.phase==="done"&&item.judge?.t1p ? getRank(item) : "—"}
          onRemove={() => removeItem(item.id)} />
      ))}

      {displayItems.length > 0 && (
        <div style={{ fontSize:11, color:"#9ca3af", textAlign:"center", marginTop:10 }}>以上為技術分析評估，不構成投資建議。</div>
      )}
    </div>
  );
}
